import { action, observable, runInAction } from "mobx";
import { PageableResult } from "./Api";
import { CommonsStore } from "../model/CommonsStore";
import { Schema } from "./Schema";
import {
  DataSource,
  Filter,
  FindAllParams,
  ListDataSource,
  LocalFindAllParams,
  OneDataSource
} from "./DataSource";

export interface LocalDataSourceOptions<T> {
  name: string;
  schema: Schema;
  source: Promise<PageableResult<T>> | T[];
  commons: CommonsStore;
}

export class LocalDataSource<T, ID> implements DataSource<T, ID> {
  public schema: Schema;
  public name: string;
  @observable public oneDataSource: OneDataSource<T, ID>;
  @observable public listDataSource: ListDataSource<T, ID>;
  public commons: CommonsStore;

  public constructor(options: LocalDataSourceOptions<T>) {
    this.schema = options.schema;
    this.name = options.name;
    this.commons = options.commons;
    this.listDataSource = new LocalListDataSource(options);
    this.oneDataSource = new LocalOneDataSource({
      ...options,
      listDataSource: this.listDataSource
    });
  }
}

export interface LocalOneDataSourceOptions
  extends LocalDataSourceOptions<Object> {
  listDataSource: ListDataSource<{}, {}>;
}

export class LocalOneDataSource<T, ID> implements OneDataSource<T, ID> {
  public schema: Schema;
  public name: string;
  @observable public one?: T;
  public persisted?: T;
  public commons: CommonsStore;
  public listDataSource: ListDataSource<{}, {}>;

  public constructor({
    name,
    schema,
    listDataSource,
    commons
  }: LocalOneDataSourceOptions) {
    this.schema = schema;
    this.name = name;
    this.listDataSource = listDataSource;
    this.commons = commons;
  }

  @action
  public change(one: T): T {
    this.one = one;
    return this.one;
  }

  @action
  public async delete(): Promise<void> {
    if (!this.one) {
      return;
    }

    await this.listDataSource.delete([this.schema.toId(this.one)]);

    runInAction(() => {
      this.one = undefined;
      this.persisted = undefined;
    });

    return;
  }

  @action
  public async download(type: string): Promise<void> {
    throw new Error("Not implemented yet");
  }

  @action
  public async upload(): Promise<void> {
    throw new Error("Not implemented yet");
  }

  @action
  public clearPkFields(): T | undefined {
    if (this.one) {
      const diff = this.one;
      this.schema.primaryKey.forEach((key: string) => {
        delete diff[key];
      });
      this.one = diff;
    }
    return this.one;
  }

  @action
  public clear(): void {
    this.one = undefined;
    this.persisted = undefined;
  }

  @action
  public async findOne(id: ID): Promise<T> {
    const filters = Object.keys(id).reduce((acc, key) => {
      acc.push({ field: key, value: id[key] });
      return acc;
    }, [] as Filter[]);
    const list = await this.listDataSource.findAll({ filters });

    if (list.content.length > 1) {
      throw new Error(
        "oneDataSource.findOne returned more than one matchnig row"
      );
    }

    if (list.content.length < 1) {
      throw new Error("notFound");
    }

    if (list.content.length === 1) {
      runInAction(() => {
        this.one = list.content[0] as T;
      });
    }

    return this.one!;
  }

  public rollback(): T | undefined {
    this.one = this.persisted;

    return this.one;
  }

  public async save(): Promise<T> {
    if (this.one) {
      throw new Error("cannot save undefined value");
    }

    this.persisted = this.one;
    return this.one!;
  }

  public async update(): Promise<T> {
    return await this.save();
  }

  public async partialUpdate(): Promise<void> {}
}

export class LocalListDataSource<T, ID> implements ListDataSource<T, ID> {
  public originalList: T[] = [];
  public schema: Schema;
  public name: string;
  public commons: CommonsStore;
  @observable public params: LocalFindAllParams;
  @observable public selected: ID[] = [];
  @observable public list: T[] = [];
  @observable public total = 0;

  public constructor({
    name,
    schema,
    source,
    commons
  }: LocalDataSourceOptions<T>) {
    this.name = name;
    this.schema = schema;
    this.params = this.withDefaults();
    this.commons = commons;
    this.initList(source);
  }

  @action
  public async delete(ids: ID[] | ID): Promise<void> {
    this.originalList = this.originalList.filter((it, idx) => {
      const id = this.schema.toId(it) as ID;
      return ids instanceof Array ? ids.indexOf(id) !== -1 : ids !== id;
    });

    return;
  }

  @action
  public async findAll(params?: FindAllParams): Promise<PageableResult<T>> {
    this.params = Object.assign(
      {},
      { page: { pageSize: 25, pageNumber: 1 } },
      params
    ) as LocalFindAllParams;
    this.params = this.withDefaults(params);
    this.list = this.originalList.filter(it => {
      for (const filter of this.params.filters) {
        if (it[filter.field] !== filter.value) {
          return false;
        }
      }

      return true;
    });

    return new PageableResult(this.list, this.total);
  }

  @action
  public async exportAll(type: string, params?: FindAllParams): Promise<void> {
    // todo

    throw new Error("not implemented yet");
  }

  @action
  public async exportSelected(type: string, ids: ID[] | ID): Promise<void> {
    // todo
    return;
  }

  @action
  public async reload(reason: string): Promise<PageableResult<T>> {
    return this.findAll(this.params);
  }

  @action
  public setSelected(id: ID): void {
    this.selected = [...this.selected, id];
  }

  @action
  public async updateRows(ids: ID[] | ID, data: {}): Promise<void> {}

  @action
  public clear() {
    this.clearFilters();
    this.clearSelected();
  }

  @action
  public clearSelected(): void {
    this.selected = [];
  }

  @action
  public clearFilters(): void {
    this.params.filters = [];
  }

  @action
  public toggleSelect(id: ID): void {
    const idx = this.selected.indexOf(id);

    if (idx >= 0) {
      this.selected = this.selected.filter((v, index) => {
        return index !== idx;
      });
    } else {
      this.selected = [...this.selected, id];
    }
  }

  @action
  public toggleSelectOne(id: ID): void {
    const idx = this.selected.indexOf(id);
    if (idx >= 0) {
      this.selected = this.selected.filter((v, index) => {
        return index !== idx;
      });
    } else {
      this.selected = [id];
    }
  }

  @action
  public toggleSelectAll(): void {
    const isEmpty = this.selected.length === 0;

    if (isEmpty) {
      this.selectAll();
    } else {
      this.selected = [];
    }
  }

  @action
  private initList(source: Promise<PageableResult<T>> | T[]) {
    if (source instanceof Array) {
      this.originalList = source;
      this.list = source;
    } else {
      source.then(result => {
        this.originalList = result.content;
        this.list = result.content;
      });
    }

    return;
  }

  @action
  private withDefaults(params?: FindAllParams): LocalFindAllParams {
    params = params || { filters: [] };
    const page = params.page || { pageSize: 25, pageNumber: 0 };
    const sort = params.sort || {
      sort: this.schema.fields[0].name,
      order: "desc"
    };
    const filters = params.filters || [];

    return { filters, page, sort };
  }

  private selectAll(): void {
    // todo: make it type safe
    this.selected = this.originalList.map(it => it as any);
  }
}
